<template>
    <v-container class="pa-5" fluid>
        <ImportAddressDialog
            :is-open="importAddressDialog"
            @on-loaded="xlsDataLoaded"
            @on-close="closeImportAddressDialog"
        />
        <ShipmentsOrderPositionDialog
            :total-weight="totalWareWeightAmount"
            :is-open="shippingOrderPositionDialog"
            :customer-contacts="customerContacts"
            :headquarters-contact="headquartersContact"
            :shipping-order-position-data="currentShipmentsOrderPosition"
            :transport-types-data="transportTypes"
            :is-transport-editable-only="!isEditable"
            @on-save="saveShipmentsOrderPosition"
            @on-close="closeShipmentsOrderPositionDialog"
        />

        <ConfirmDialog
            :is-open="confirmRemoveShipmentsOrderPositionDialog"
            :data="currentShipmentsOrderPosition"
            @on-confirm="removeShipmentsOrderPosition(currentShipmentsOrderPosition)"
            @on-abort="closeConfirmRemoveShipmentsOrderPositionDialog"
        >
            <template #title>
                Usuwanie adresu
            </template>
            <template #content>
                Czy na pewno chcesz usunąć adres?
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmCopyShipmentsOrderPositionDialog"
            :data="currentShipmentsOrderPosition"
            @on-confirm="copyShipmentsOrderPositionDialog"
            @on-abort="closeConfirmCopyShipmentsOrderPositionDialog"
        >
            <template #title>
                Kopiowanie adresu
            </template>
            <template #content>
                Czy chcesz skopiować dane adresowe na pozostałe pozycje ZS?
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmLeaveDialog"
            @on-confirm="acceptConfirmLeaveDialog"
            @on-abort="closeConfirmLeaveDialog"
        >
            <template #title>
                Opuścić stronę?
            </template>
            <template #content>
                Zlecenie spedycyjne nie zostało zapisane. Czy chcesz opuścić stronę?
            </template>
        </ConfirmDialog>

        <PackageDialog
            :is-open="shippingOrderPositionPackageDialog"
            :package-data="currentShipmentsOrderPositionPackage"
            :box-types-data="boxTypes"
            :package-types-data="packageTypes"
            @on-close="closeShipmentsOrderPositionPackageDialog"
            @on-save="saveShipmentsOrderPositionPackage"
        />

        <ConfirmDialog
            :is-open="confirmRemoveShipmentsOrderPositionPackageDialog"
            :data="currentShipmentsOrderPositionPackage"
            @on-confirm="removeShipmentsOrderPositionPackage(currentShipmentsOrderPositionPackage)"
            @on-abort="closeConfirmRemoveShipmentsOrderPositionPackageDialog"
        >
            <template #title>
                Usuwanie pakowania
            </template>
            <template #content>
                Czy na pewno chcesz usunąć pakowanie?
            </template>
        </ConfirmDialog>

        <ProductDialog
            :is-open="shippingOrderPositionPackageWareDialog"
            :ware-data="currentShipmentsOrderPositionPackageWare"
            :shipping-order-data="shippingOrder"
            :package-data="currentShipmentsOrderPositionPackage"
            :products-data="shippingOrder.products"
            :package-types-data="getCartonsList"
            @on-close="closeShipmentsOrderPositionPackageWareDialog"
            @on-save="saveShipmentsOrderPositionPackageWare"
        />

        <ConfirmDialog
            :is-open="confirmRemoveShipmentsOrderPositionPackageWareDialog"
            :data="currentShipmentsOrderPositionPackageWare"
            @on-confirm="removeShipmentsOrderPositionPackageWare(currentShipmentsOrderPositionPackageWare)"
            @on-abort="closeConfirmRemoveShipmentsOrderPositionPackageWareDialog"
        >
            <template #title>
                Usuwanie towaru
            </template>
            <template #content>
                Czy na pewno chcesz usunąć towar?
            </template>
        </ConfirmDialog>

        <page-title>
            Zlecenie spedycyjne {{ shippingOrder.number }}
            <span :class="shippingOrderStatusClass" class="text-uppercase text-body-2">
                {{ shippingOrder.status.name }}
            </span>
        </page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    :disabled="actionLoading.saveShipmentsOrder || actionLoading.sendShipmentsOrder"
                    @click="closeShipmentsOrder"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    <template v-if="routeParentName !== 'jobsJob'">
                        Powrót do listy
                    </template>
                    <template v-if="routeParentName === 'jobsJob'">
                        Powrót do zlecenia
                    </template>
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-container class="pa-0">
                                <v-row>
                                    <v-col cols="12">
                                        <h2 class="text-h6">
                                            Parametry zlecenia
                                        </h2>
                                    </v-col>
                                    <v-col cols="12">
                                        <v-card>
                                            <v-card-text>
                                                <table class="table table--simple">
                                                    <tbody>
                                                        <tr>
                                                            <th>Data utworzenia:</th>
                                                            <td>{{ formatDate(shippingOrder.createdAt) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Handlowiec:</th>
                                                            <td>{{ shippingOrder.traderFirstName }} {{ shippingOrder.traderLastName }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Kwota założona za transport:</th>
                                                            <td>{{ formatPrice(shippingOrder.plannedCost) }} zł</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Cena netto zamówień:</th>
                                                            <td>{{ formatPrice(shippingOrder.orderNetPrice) }} zł</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Koszt materiałów:</th>
                                                            <td>{{ formatPrice(shippingOrder.productionCost) }} zł</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-container class="pa-0">
                                <v-row>
                                    <v-col cols="12">
                                        <h2 class="text-h6">
                                            Dane klienta
                                        </h2>
                                    </v-col>
                                    <v-col cols="12">
                                        <v-card>
                                            <v-card-text>
                                                <table class="table table--simple">
                                                    <tbody>
                                                        <tr>
                                                            <th>Nazwa klienta:</th>
                                                            <td>{{ hasShipmentsOrderCustomer && shippingOrder.shippingOrderCustomer.name ? shippingOrder.shippingOrderCustomer.name : '-' }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>NIP:</th>
                                                            <td>{{ hasShipmentsOrderCustomer && shippingOrder.shippingOrderCustomer.taxNumber ? shippingOrder.shippingOrderCustomer.taxNumber : '-' }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Adres:</th>
                                                            <td>
                                                                {{ clientAddress }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th>Telefon:</th>
                                                            <td>{{ hasShipmentsOrderCustomer && shippingOrder.shippingOrderCustomer.phoneNumber ? shippingOrder.shippingOrderCustomer.phoneNumber : '-' }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                    </v-row>
                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Uwagi
                            </h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-textarea
                                        v-model="shippingOrder.description"
                                        hide-details
                                        outlined
                                        auto-grow
                                        rows="1"
                                        dense
                                        :disabled="!isEditable"
                                    />
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Zamówienia - koszty
                            </h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="9">
                            <v-card>
                                <v-card-text>
                                    <v-row v-for="(good, index) in shippingOrderGoodsPrices" :key="index">
                                        <v-col cols="12" sm="3" class="d-flex justify-center mt-2">
                                            <strong>{{ good.productionNumber }}</strong>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-text-field
                                                v-model="good.orderNetPrice"
                                                label="Cena netto"
                                                suffix="(zł)"
                                                outlined
                                                dense
                                                :error-messages="orderNetPriceErrors(index)"
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-text-field
                                                v-model="good.productionCost"
                                                label="Koszt materiałów"
                                                suffix="(zł)"
                                                outlined
                                                dense
                                                :error-messages="productionCostErrors(index)"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-tabs
                                    v-model="tabs"
                                    slider-color="blue"
                                    grow
                                >
                                    <v-tab>
                                        Towary
                                    </v-tab>
                                    <v-tab>
                                        Adresy
                                    </v-tab>
                                    <v-tabs-items v-model="tabs">
                                        <v-divider />
                                        <v-tab-item>
                                            <v-card flat>
                                                <v-card-text>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-data-table
                                                                :headers="goodsHeaders"
                                                                :items="goodsItems"
                                                                dense
                                                                item-key="id"
                                                                sort-by="name"
                                                                :mobile-breakpoint="0"
                                                                :items-per-page="-1"
                                                                hide-default-footer
                                                            >
                                                                <template #item.campaignNumber="{item}">
                                                                    {{ parseEmptyValue(item.campaignNumber) }}
                                                                </template>
                                                                <template #item.productionNumber="{item}">
                                                                    {{ parseEmptyValue(item.productionNumber) }}
                                                                </template>
                                                                <template #item.name="{item}">
                                                                    {{ item.name }}
                                                                </template>
                                                                <template #item.amount="{item}">
                                                                    {{ item.amount }}
                                                                </template>
                                                                <template slot="no-results">
                                                                    <p class="text-center">
                                                                        Brak danych
                                                                    </p>
                                                                </template>
                                                            </v-data-table>
                                                        </v-col>
                                                    </v-row>
                                                </v-card-text>
                                            </v-card>
                                        </v-tab-item>
                                        <v-tab-item>
                                            <v-card flat>
                                                <v-card-text>
                                                    <v-row align="center">
                                                        <v-col cols="auto">
                                                            <h2 class="text-h6">
                                                                Adresy
                                                            </h2>
                                                        </v-col>
                                                        <v-col cols="auto">
                                                            <v-btn
                                                                v-if="isEditable"
                                                                color="secondary"
                                                                fab
                                                                small
                                                                class="elevation-1"
                                                                data-sentry-label="Dodaj adres"
                                                                @click="showShipmentsOrderPositionDialog(null, $event)"
                                                            >
                                                                <v-icon>
                                                                    mdi-plus
                                                                </v-icon>
                                                            </v-btn>
                                                        </v-col>
                                                        <v-row justify="end">
                                                            <v-col cols="auto">
                                                                <v-btn
                                                                    color="primary"
                                                                    outlined
                                                                    medium
                                                                    class="mr-4"
                                                                    data-sentry-label="importuj adresy"
                                                                    @click="showImportAddressDialog"
                                                                >
                                                                    Importuj adresy
                                                                </v-btn>
                                                            </v-col>
                                                        </v-row>
                                                    </v-row>
                                                    <v-row v-if="shippingOrderPositions.length === 0">
                                                        <v-col cols="12">
                                                            <v-alert
                                                                type="info"
                                                                text
                                                                border="left"
                                                            >
                                                                Brak adresów.
                                                            </v-alert>
                                                        </v-col>
                                                    </v-row>
                                                    <v-row v-else>
                                                        <v-col cols="12">
                                                            <v-expansion-panels
                                                                accordion
                                                                multiple
                                                            >
                                                                <v-expansion-panel v-for="(shippingOrderPosition, shippingOrderPositionIndex) in shippingOrderPositions" :key="shippingOrderPositionIndex">
                                                                    <v-expansion-panel-header
                                                                        :class="{'error--text': shippingOrderPositionError(shippingOrderPositionIndex)}"
                                                                    >
                                                                        <v-row no-gutters align="center" dense>
                                                                            <v-col cols="auto">
                                                                                <span>
                                                                                    <h1 class="text-subtitle-1">
                                                                                        <template>
                                                                                            <strong>{{ shippingOrderPositionIndex + 1 }}.</strong>
                                                                                            {{ parseAddress(shippingOrderPosition) }}
                                                                                        </template>
                                                                                    </h1>
                                                                                </span>
                                                                            </v-col>
                                                                            <v-col>
                                                                                <v-container fluid class="pa-0">
                                                                                    <v-row justify="end" dense>
                                                                                        <v-tooltip v-if="shippingOrderPositions.length > 1 && isEditable" :open-delay="tooltipDelay" top>
                                                                                            <template #activator="{on}">
                                                                                                <v-btn
                                                                                                    icon
                                                                                                    data-sentry-label="Kopiuj Adres"
                                                                                                    v-on="on"
                                                                                                    @click.native="showConfirmCopyShipmentsOrderPositionDialog(shippingOrderPosition, $event)"
                                                                                                >
                                                                                                    <v-icon color="green">
                                                                                                        mdi-content-copy
                                                                                                    </v-icon>
                                                                                                </v-btn>
                                                                                            </template>
                                                                                            <span>Kopiuj Adres</span>
                                                                                        </v-tooltip>
                                                                                        <v-tooltip :open-delay="tooltipDelay" top>
                                                                                            <template #activator="{on}">
                                                                                                <v-btn
                                                                                                    icon
                                                                                                    v-on="on"
                                                                                                    @click.native="showShipmentsOrderPositionDialog(shippingOrderPosition, $event)"
                                                                                                >
                                                                                                    <v-icon color="blue">
                                                                                                        mdi-pencil
                                                                                                    </v-icon>
                                                                                                </v-btn>
                                                                                            </template>
                                                                                            <span>Edycja</span>
                                                                                        </v-tooltip>
                                                                                        <v-tooltip v-if="isEditable" :open-delay="tooltipDelay" top>
                                                                                            <template #activator="{on}">
                                                                                                <v-btn
                                                                                                    icon
                                                                                                    data-sentry-label="Usuń adres"
                                                                                                    v-on="on"
                                                                                                    @click="showConfirmRemoveShipmentsOrderPositionDialog(shippingOrderPosition, $event)"
                                                                                                >
                                                                                                    <v-icon color="pink">
                                                                                                        mdi-delete
                                                                                                    </v-icon>
                                                                                                </v-btn>
                                                                                            </template>
                                                                                            <span>Usuń Adres</span>
                                                                                        </v-tooltip>
                                                                                    </v-row>
                                                                                </v-container>
                                                                            </v-col>
                                                                        </v-row>
                                                                    </v-expansion-panel-header>
                                                                    <v-divider />
                                                                    <v-expansion-panel-content>
                                                                        <v-row>
                                                                            <v-col cols="12" md="5">
                                                                                <table class="table table--simple">
                                                                                    <tbody>
                                                                                        <tr>
                                                                                            <th>Planowana data wysyłki:</th>
                                                                                            <td>{{ formatDate(shippingOrderPosition.expeditionDateTime) }}</td>
                                                                                        </tr>
                                                                                        <tr>
                                                                                            <th>Data dostawy:</th>
                                                                                            <td>{{ formatDate(shippingOrderPosition.deliveryDateTime) }}</td>
                                                                                        </tr>
                                                                                        <tr>
                                                                                            <th>Środek transportu:</th>
                                                                                            <td>{{ parseEmptyValue(shippingOrderPosition.transportType) }}</td>
                                                                                        </tr>
                                                                                    </tbody>
                                                                                </table>
                                                                            </v-col>
                                                                            <v-col cols="12" md="5" offset-md="2">
                                                                                <table class="table table--simple">
                                                                                    <tbody>
                                                                                        <tr>
                                                                                            <th>Osoba kontaktowa:</th>
                                                                                            <td>
                                                                                                {{ parseEmptyValue(shippingOrderPosition.contactPerson) }}
                                                                                                <template v-if="shippingOrderPosition.phoneNumber">
                                                                                                    <br> tel. {{ shippingOrderPosition.phoneNumber }}
                                                                                                </template>
                                                                                            </td>
                                                                                        </tr>
                                                                                        <tr>
                                                                                            <th>Koszt:</th>
                                                                                            <td>{{ parseEmptyValue(shippingOrderPosition.cost) }} zł</td>
                                                                                        </tr>
                                                                                    </tbody>
                                                                                </table>
                                                                            </v-col>
                                                                            <v-col cols="12">
                                                                                <v-container class="pa-0">
                                                                                    <v-row align="center">
                                                                                        <v-col cols="auto">
                                                                                            <strong>Pakowanie</strong>
                                                                                        </v-col>
                                                                                        <v-col cols="auto">
                                                                                            <v-btn
                                                                                                v-if="isEditable"
                                                                                                color="secondary"
                                                                                                fab
                                                                                                x-small
                                                                                                class="elevation-1"
                                                                                                data-sentry-label="Dodaj pakowanie"
                                                                                                @click="showShipmentsOrderPositionPackageDialog(null, shippingOrderPositionIndex, $event)"
                                                                                            >
                                                                                                <v-icon>
                                                                                                    mdi-plus
                                                                                                </v-icon>
                                                                                            </v-btn>
                                                                                        </v-col>
                                                                                        <v-col v-if="shippingOrderPosition.shippingOrderPositionPackages.length === 0" cols="12">
                                                                                            <v-alert
                                                                                                type="info"
                                                                                                text
                                                                                                border="left"
                                                                                            >
                                                                                                Brak pakowania.
                                                                                            </v-alert>
                                                                                        </v-col>
                                                                                        <v-col v-else cols="12">
                                                                                            <v-expansion-panels
                                                                                                accordion
                                                                                                multiple
                                                                                            >
                                                                                                <v-expansion-panel v-for="(shippingOrderPositionPackage, shippingOrderPositionPackageIndex) in shippingOrderPosition.shippingOrderPositionPackages" :key="shippingOrderPositionPackage.id">
                                                                                                    <v-expansion-panel-header
                                                                                                        :class="{'error--text': shippingOrderPositionPackageError(shippingOrderPositionIndex, shippingOrderPositionPackageIndex)}"
                                                                                                    >
                                                                                                        <v-row align="center" dense>
                                                                                                            <v-col cols="12" sm="3">
                                                                                                                Sposób pakowania: <strong>{{ shippingOrderPositionPackage.boxType.name }}</strong>
                                                                                                            </v-col>
                                                                                                            <v-col cols="12" sm="5">
                                                                                                                Rodzaj opakowania: <strong>{{ shippingOrderPositionPackage.packageType.name }}</strong>
                                                                                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                                                                                    <template #activator="{on}">
                                                                                                                        <v-icon color="primary" size="20" class="ml-2" v-on="on">
                                                                                                                            mdi-axis-arrow-info
                                                                                                                        </v-icon>
                                                                                                                    </template>
                                                                                                                    <span>
                                                                                                                        D: {{ shippingOrderPositionPackage.length }} <br>
                                                                                                                        SZ: {{ shippingOrderPositionPackage.width }} <br>
                                                                                                                        W: {{ shippingOrderPositionPackage.height }}
                                                                                                                    </span>
                                                                                                                </v-tooltip>
                                                                                                            </v-col>
                                                                                                            <v-col cols="12" sm="1">
                                                                                                                Ilość: <strong>{{ shippingOrderPositionPackage.amount }}</strong>
                                                                                                            </v-col>
                                                                                                            <v-col cols="12" sm="2">
                                                                                                                Waga brutto: <strong>{{ formatWeight(roundWeight(bruttoWeight(shippingOrderPositionPackage))) }}</strong>
                                                                                                            </v-col>
                                                                                                            <v-col v-if="isEditable" cols="12" sm="auto">
                                                                                                                <v-container fluid class="pa-0">
                                                                                                                    <v-row justify="end" dense>
                                                                                                                        <v-tooltip :open-delay="tooltipDelay" top>
                                                                                                                            <template #activator="{on}">
                                                                                                                                <v-btn
                                                                                                                                    icon
                                                                                                                                    data-sentry-label="Edycja pakowania"
                                                                                                                                    v-on="on"
                                                                                                                                    @click.native="showShipmentsOrderPositionPackageDialog(shippingOrderPositionPackage, shippingOrderPositionIndex, $event)"
                                                                                                                                >
                                                                                                                                    <v-icon color="blue">
                                                                                                                                        mdi-pencil
                                                                                                                                    </v-icon>
                                                                                                                                </v-btn>
                                                                                                                            </template>
                                                                                                                            <span>Edycja</span>
                                                                                                                        </v-tooltip>
                                                                                                                        <v-tooltip :open-delay="tooltipDelay" top>
                                                                                                                            <template #activator="{on}">
                                                                                                                                <v-btn
                                                                                                                                    icon
                                                                                                                                    data-sentry-label="Usuń komponent"
                                                                                                                                    v-on="on"
                                                                                                                                    @click.native="showConfirmRemoveShipmentsOrderPositionPackageDialog(shippingOrderPositionPackage, shippingOrderPositionIndex, $event)"
                                                                                                                                >
                                                                                                                                    <v-icon color="pink">
                                                                                                                                        mdi-delete
                                                                                                                                    </v-icon>
                                                                                                                                </v-btn>
                                                                                                                            </template>
                                                                                                                            <span>Usuń adres</span>
                                                                                                                        </v-tooltip>
                                                                                                                    </v-row>
                                                                                                                </v-container>
                                                                                                            </v-col>
                                                                                                        </v-row>
                                                                                                    </v-expansion-panel-header>
                                                                                                    <v-divider />
                                                                                                    <v-expansion-panel-content>
                                                                                                        <v-row justify="end" dense>
                                                                                                            <v-col cols="12" sm="10">
                                                                                                                <v-row align="center">
                                                                                                                    <v-col cols="auto">
                                                                                                                        <strong>Towary</strong>
                                                                                                                    </v-col>
                                                                                                                    <v-col cols="auto">
                                                                                                                        <v-btn
                                                                                                                            v-if="isEditable"
                                                                                                                            color="secondary"
                                                                                                                            fab
                                                                                                                            x-small
                                                                                                                            class="elevation-1"
                                                                                                                            data-sentry-label="Dodaj towar"
                                                                                                                            @click="showShipmentsOrderPositionPackageWareDialog(null, shippingOrderPositionPackageIndex, shippingOrderPositionIndex, shippingOrderPositionPackage, $event)"
                                                                                                                        >
                                                                                                                            <v-icon>
                                                                                                                                mdi-plus
                                                                                                                            </v-icon>
                                                                                                                        </v-btn>
                                                                                                                    </v-col>
                                                                                                                </v-row>
                                                                                                                <v-data-table
                                                                                                                    :items="shippingOrderPositionPackage.shippingOrderPositionPackageWares"
                                                                                                                    :headers="isEditable ? packageWaresHeaders : readOnlyPackageWaresHeaders"
                                                                                                                    disable-pagination
                                                                                                                    :mobile-breakpoint="0"
                                                                                                                    hide-default-footer
                                                                                                                >
                                                                                                                    <template #item.wareName="{item}">
                                                                                                                        <span :class="{'error--text': shippingOrderPositionPackageWaresError(shippingOrderPositionIndex, shippingOrderPositionPackageIndex, item)}">
                                                                                                                            {{ item.wareName }}
                                                                                                                        </span>
                                                                                                                    </template>
                                                                                                                    <template #item.wareAmount="{item}">
                                                                                                                        <span :class="{'error--text': shippingOrderPositionPackageWaresError(shippingOrderPositionIndex, shippingOrderPositionPackageIndex, item)}">
                                                                                                                            {{ item.wareAmount }} / {{ productAmount(item) }}
                                                                                                                        </span>
                                                                                                                    </template>
                                                                                                                    <template #item.wareUnitPackageQuantity="{item}">
                                                                                                                        <span :class="{'error--text': shippingOrderPositionPackageWaresError(shippingOrderPositionIndex, shippingOrderPositionPackageIndex, item)}">
                                                                                                                            <template v-if="item.wareUnitPackageQuantity !== null">
                                                                                                                                {{ item.wareUnitPackageQuantity }}
                                                                                                                            </template>
                                                                                                                            <template v-else>
                                                                                                                                0
                                                                                                                            </template>
                                                                                                                        </span>
                                                                                                                    </template>
                                                                                                                    <template #item.weight="{item}">
                                                                                                                        <span :class="{'error--text': shippingOrderPositionPackageWaresError(shippingOrderPositionIndex, shippingOrderPositionPackageIndex, item)}">
                                                                                                                            {{ formatWeight(roundWeight(totalWareWeight(item))) }} / {{ formatWeight(roundWeight(totalWareWeight(item) + boxesWareWeight(item))) }}
                                                                                                                        </span>
                                                                                                                    </template>
                                                                                                                    <template #item.actions="{item}">
                                                                                                                        <div class="d-inline-flex">
                                                                                                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                                                                                                <template #activator="{on}">
                                                                                                                                    <v-btn
                                                                                                                                        icon
                                                                                                                                        data-sentry-label="Edycja towaru"
                                                                                                                                        v-on="on"
                                                                                                                                        @click.native="showShipmentsOrderPositionPackageWareDialog(item, shippingOrderPositionPackageIndex, shippingOrderPositionIndex, shippingOrderPositionPackage, $event)"
                                                                                                                                    >
                                                                                                                                        <v-icon color="blue">
                                                                                                                                            mdi-pencil
                                                                                                                                        </v-icon>
                                                                                                                                    </v-btn>
                                                                                                                                </template>
                                                                                                                                <span>Edycja</span>
                                                                                                                            </v-tooltip>
                                                                                                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                                                                                                <template #activator="{on}">
                                                                                                                                    <v-btn
                                                                                                                                        icon
                                                                                                                                        v-on="on"
                                                                                                                                        @click.native="showConfirmRemoveShipmentsOrderPositionPackageWareDialog(item, shippingOrderPositionPackageIndex, shippingOrderPositionIndex, shippingOrderPositionPackage, $event)"
                                                                                                                                    >
                                                                                                                                        <v-icon color="pink">
                                                                                                                                            mdi-delete
                                                                                                                                        </v-icon>
                                                                                                                                    </v-btn>
                                                                                                                                </template>
                                                                                                                                <span>Usuń</span>
                                                                                                                            </v-tooltip>
                                                                                                                        </div>
                                                                                                                    </template>
                                                                                                                </v-data-table>
                                                                                                            </v-col>
                                                                                                        </v-row>
                                                                                                    </v-expansion-panel-content>
                                                                                                </v-expansion-panel>
                                                                                            </v-expansion-panels>
                                                                                        </v-col>
                                                                                    </v-row>
                                                                                </v-container>
                                                                            </v-col>
                                                                        </v-row>
                                                                    </v-expansion-panel-content>
                                                                </v-expansion-panel>
                                                            </v-expansion-panels>
                                                        </v-col>
                                                    </v-row>
                                                </v-card-text>
                                            </v-card>
                                        </v-tab-item>
                                    </v-tabs-items>
                                </v-tabs>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row v-if="shippingOrderAttachment.length > 0">
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Rozdzielniki
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text class="pa-0 px-2">
                                    <v-list dense>
                                        <v-list-item
                                            v-for="(item, index) in uploadedFiles"
                                            :key="index"
                                            @click="openInNewTab(item)"
                                        >
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ formatName(item) }}
                                                </v-list-item-title>
                                            </v-list-item-content>
                                            <v-list-item-icon>
                                                <v-icon color="primary">
                                                    mdi-download
                                                </v-icon>
                                            </v-list-item-icon>
                                        </v-list-item>
                                    </v-list>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                v-if="isEditable"
                                color="success"
                                medium
                                :loading="actionLoading.saveShipmentsOrder"
                                :disabled="actionLoading.sendShipmentsOrder"
                                data-sentry-label="Zapisz"
                                @click="saveShipmentsOrder"
                            >
                                Zapisz
                            </v-btn>
                            <v-btn
                                v-if="isEditable"
                                color="primary"
                                medium
                                class="ml-3"
                                data-sentry-label="Wyślij do XL"
                                :loading="actionLoading.sendShipmentsOrder"
                                :disabled="isReadyToSent || actionLoading.saveShipmentsOrder"
                                @click="sendShipmentsOrder"
                            >
                                Wyślij do XL
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/store'
import { appConfig } from '@/config'

import PageTitle from '@/components/common/PageTitle'

import attachment from '@/mixins/attachment'
import dateFormatter from '@/mixins/dateFormatter'
import parse from '@/mixins/parse'
import priceFormatter from '@/mixins/priceFormatter'
import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'
import packages from '@/mixins/packages'

import { required, helpers } from 'vuelidate/lib/validators'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { twoDigitsAfterDecimalWithCommaAndSpaces, isGreaterThanZero } from '@/utils/customValidators'
import { findIndex } from 'lodash'

export default {
    name: 'ShipmentsOrder',
    components: {
        PageTitle,
        ShipmentsOrderPositionDialog: () => import('./ShipmentsOrderPositionDialog.vue'),
        ConfirmDialog: () => import('@/components/common/ConfirmDialog.vue'),
        PackageDialog: () => import('./PackageDialog.vue'),
        ProductDialog: () => import('./ProductDialog.vue'),
        ImportAddressDialog: () => import('./ImportAddressDialog.vue'),
    },
    mixins: [
        attachment,
        dateFormatter,
        parse,
        priceFormatter,
        tooltipDelay,
        weightFormatter,
        packages,
    ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('customers/getCountries'),
            store.dispatch('shipments/shipmentsOrders/getShipmentsOrder', to.params.shippingOrderId)
        ])
            .then(responses => {
                next(vm => {
                    vm.shippingOrder = responses[1].data
                    vm.shippingOrderGoodsPrices = JSON.parse(JSON.stringify(vm.getPrices(vm.shippingOrder)))
                    vm.indexedShipmentsOrder()
                    vm.createUploadedUrls(vm.shippingOrder.shippingOrderAttachment)
                    vm.routeParentName = to.matched.slice(-1)[0]?.parent.meta.parentName
                    vm.goodsItems = JSON.parse(JSON.stringify(responses[1].data.products))
                    vm.goods = vm.getGoods(vm.goodsItems)
                    vm.getPackagesTypes()
                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    beforeRouteLeave(to, from, next) {
        if (this.hasShipmentsOrderChanges) {
            this.confirmLeaveDialog = true
            this.confirmLeaveDialogNextRouteName = to.name
        } else {
            next()
        }
    },
    data() {
        return {
            totalWareWeightAmount: null,
            shippingOrder: {
                status: {},
                createdBy: null,
                createdAt: null,
                shippingOrderCustomer: {
                    name: null,
                    street: null,
                    postCode: null,
                    city: null,
                    taxNumber: null,
                    phoneNumber: null
                },
                shippingOrderPositions: [],
                shippingOrderAttachment: []
            },
            shippingOrderGoodsPrices: [],
            shippingOrderPositionDialog: false,
            customer: null,
            customerContacts: [],
            actionLoading: {
                customerContacts: false,
                saveShipmentsOrder: false,
                sendShipmentsOrder: false,
                saveChangeGoodItem: false
            },
            confirmRemoveShipmentsOrderPositionDialog: false,
            confirmCopyShipmentsOrderPositionDialog: false,
            currentShipmentsOrderPositionIndex: null,
            currentShipmentsOrderPosition: null,
            currentShipmentsOrderPositionPackage: null,
            currentShipmentsOrderPositionPackageIndex: null,
            shippingOrderPositionPackageDialog: false,
            confirmRemoveShipmentsOrderPositionPackageDialog: false,
            currentShipmentsOrderPositionPackageWare: null,
            shippingOrderPositionPackageWareDialog: false,
            confirmRemoveShipmentsOrderPositionPackageWareDialog: false,
            hasShipmentsOrderChanges: false,
            confirmLeaveDialog: false,
            confirmLeaveDialogNextRouteName: null,
            transportTypes: [],
            routeParentName: null,
            headquartersContact: null,
            tabs: [],
            goodsItems: [],
            goods: [],
            shippingOrderGoodItem: null,
            importAddressDialog: false,
            packageWaresHeaders: [
                {
                    text: 'Towar',
                    value: 'wareName',
                    align: 'left',
                    sortable: false,
                    showOnRead: true
                },
                {
                    text: 'Nakład',
                    value: 'wareAmount',
                    align: 'center',
                    sortable: false,
                    width: 150,
                    showOnRead: true
                },
                {
                    text: 'Ilość kartonów',
                    align: 'center',
                    sortable: false,
                    value: 'wareUnitPackageQuantity',
                    showOnRead: true
                },
                {
                    text: 'Netto / Brutto',
                    value: 'weight',
                    align: 'center',
                    sortable: false,
                    width: 150,
                    showOnRead: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100,
                    showOnRead: false
                }
            ],
            goodsHeaders: [
                {
                    text: 'Kampania',
                    value: 'campaignNumber',
                    sortable: false
                },
                {
                    text: 'Zamówienie',
                    value: 'productionNumber',
                    sortable: false
                },
                {
                    text: 'Produkt',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Do wysyłki',
                    value: 'amount',
                    sortable: false,
                },
            ]
        }
    },
    computed: {
        readOnlyPackageWaresHeaders() {
            return this.packageWaresHeaders.filter(item => item.showOnRead)
        },
        isEditable() {
            return this.shippingOrder.statusId === 10 // New shipping order
        },
        isReadyToSent() {
            return !this.shippingOrder.isProperlyValidated || this.hasShipmentsOrderChanges
        },
        clientAddress() {
            const customer = this.shippingOrder.shippingOrderCustomer

            return customer !== null && customer.street ? `${customer.street}, ${customer.postCode} ${customer.city }` : '-'
        },
        shippingOrderPositions() {
            return this.shippingOrder.shippingOrderPositions
        },
        shippingOrderAttachment() {
            return this.shippingOrder.shippingOrderAttachment
        },
        shippingOrderStatusClass() {
            let shippingOrderStatusClass = ''

            switch (this.shippingOrder.statusId) {
                case 10: // New shipping order
                    shippingOrderStatusClass = 'info--text'
                    break
                case 90: // Approved shipping order
                    shippingOrderStatusClass = 'success--text'
                    break
                default:
                    break
            }

            return shippingOrderStatusClass
        },
        hasShipmentsOrderCustomer() {
            return this.shippingOrder.shippingOrderCustomer !== null
        },
        getCartonsList() {
            return this.packageTypes.filter(item => item?.boxType?.name == 'Karton')
        },
        countries() {
            return this.$store.state.customers.customers.countries
        },
        
    },
    validations() {
        const wareAmountHelper = () => {
            return helpers.withParams({ type: 'maxWeightCapacityHelper' }, (shippingOrderPositionPackageWaresModel) => {
                const product = this.shippingOrder.products.find(product => product.name === shippingOrderPositionPackageWaresModel.wareName)

                let assignedAmount = 0

                this.shippingOrder.shippingOrderPositions.forEach(shippingOrderPosition => {
                    shippingOrderPosition.shippingOrderPositionPackages.forEach(shippingOrderPositionPackage => {
                        shippingOrderPositionPackage.shippingOrderPositionPackageWares.forEach(shippingOrderPositionPackageWare => {
                            if (shippingOrderPositionPackageWare.wareName === product.name) {
                                assignedAmount += parseInt(shippingOrderPositionPackageWare.wareAmount)
                            }
                        })
                    })
                })

                return shippingOrderPositionPackageWaresModel.wareAmount <= product.amount && assignedAmount === product.amount
            })
        }
        const wareUnitPackageQuantityHelper = () => {
            return helpers.withParams({ type: 'maxWeightCapacityHelper' }, (ware) => { 
                if (!ware.wareUnitPackageTypeId || (!ware.wareUnitPackageQuantity && ware.wareUnitPackageTypeId !== 49)) {
                    return false
                }

                return true
            })
        }

        const rules = {
            shippingOrder: {
                shippingOrderPositions: {
                    $each: {
                        companyName: {
                            required
                        },
                        contactPerson: {
                            required
                        },
                        street: {
                            required
                        },
                        postCode: {
                            required
                        },
                        city: {
                            required
                        },
                        country: {
                            required
                        },
                        phoneNumber: {
                            required
                        },
                        transportType: {
                            required
                        },
                        cost: {
                            required
                        },
                        expeditionDateTime: {
                            required
                        },
                        deliveryDateTime: {
                            required
                        },
                        shippingOrderPositionPackages: {
                            $each: {
                                shippingOrderPositionPackageWares: {
                                    $each: {
                                        wareAmount: wareAmountHelper(),
                                        wareUnitPackageQuantity: wareUnitPackageQuantityHelper(),
                                    }
                                }
                            }
                        }
                    }
                }
            },
            shippingOrderGoodsPrices: {
                $each: {
                    orderNetPrice: {
                        required,
                        twoDigitsAfterDecimalWithCommaAndSpaces,
                        isGreaterThanZero
                    },
                    productionCost: {
                        required,
                        twoDigitsAfterDecimalWithCommaAndSpaces,
                        isGreaterThanZero
                    }
                }
            }
        }

        return rules
    },
    methods: {
        getTotalWeightForPosition(shippingOrderPosition) {
            let totalWeight = 0

            shippingOrderPosition.shippingOrderPositionPackages.forEach(packageItem => {
                packageItem.shippingOrderPositionPackageWares.forEach(ware => {
                    totalWeight += this.totalWareWeight(ware)
                    totalWeight += this.boxesWareWeight(ware)
                })
                totalWeight += packageItem.packageWeight * packageItem.amount
            })

            return totalWeight
        },
        orderNetPriceErrors(index) {
            const errors = []

            if (!this.$v.shippingOrderGoodsPrices || !this.$v.shippingOrderGoodsPrices.$each[index]) return
            if (!this.$v.shippingOrderGoodsPrices.$each[index].orderNetPrice.$dirty) return
            if (!this.$v.shippingOrderGoodsPrices.$each[index].orderNetPrice.required || !this.$v.shippingOrderGoodsPrices.$each[index].orderNetPrice.isGreaterThanZero) {
                errors.push('Proszę podać cenę netto większą niż 0.')
            }
            !this.$v.shippingOrderGoodsPrices.$each[index].orderNetPrice.twoDigitsAfterDecimalWithCommaAndSpaces && errors.push('Niepoprawny format.')
            
            return errors
        },
        productionCostErrors(index) {
            const errors = []

            if (!this.$v.shippingOrderGoodsPrices || !this.$v.shippingOrderGoodsPrices.$each[index]) return
            if (!this.$v.shippingOrderGoodsPrices.$each[index].productionCost.$dirty) return
            if (!this.$v.shippingOrderGoodsPrices.$each[index].productionCost.required || !this.$v.shippingOrderGoodsPrices.$each[index].productionCost.isGreaterThanZero) {
                errors.push('Proszę podać koszt materiałów.')
            }
            !this.$v.shippingOrderGoodsPrices.$each[index].productionCost.twoDigitsAfterDecimalWithCommaAndSpaces && errors.push('Niepoprawny format.')
            
            return errors
        },
        showImportAddressDialog() {
            this.importAddressDialog = true
            this.currentAction = null
        },
        xlsDataLoaded({data, fileName, payload}) {
            this.xlsData = data.filter(i => i.length > 0)
            this.fileName = fileName

            this.generateAddresses(payload)
        },
        closeImportAddressDialog() {
            this.importAddressDialog = false
        },
        generateAddresses(payload) {
            if (this.xlsData) {

                const data = this.xlsData

                data.forEach((row, i) => {
                    if (i > 0) {
                        const shippingOrderPositionData = {}

                        shippingOrderPositionData['companyName'] = row[0]
                        shippingOrderPositionData['street'] = row[1]
                        shippingOrderPositionData['postCode'] = row[2]
                        shippingOrderPositionData['city'] = row[3]
                        shippingOrderPositionData['contactPerson'] = payload.contactPerson
                        shippingOrderPositionData['phoneNumber'] = payload.phoneNumber
                        shippingOrderPositionData['shippingOrderPositionPackages'] = []

                        const index = findIndex(this.countries, { 'name': row[4] })

                        shippingOrderPositionData['countryId'] = this.countries[index].id
                        shippingOrderPositionData['country'] = this.countries[index]

                        this.shippingOrder.shippingOrderPositions.push(shippingOrderPositionData)
                        this.indexedShipmentsOrderPositions()
                    }
                })
            }
        },
        getGoods(shippingOrderProducts) {
            return shippingOrderProducts.map(
                ({ id, addWastePaper, name }) => ({ id, addWastePaper, name })
            )
        },
        savePaperWasteChanges() {
            if (
                this.shippingOrder.shippingOrderGood &&
                this.shippingOrder.shippingOrderGood.shippingOrderGoodsItems
            ) {
                this.shippingOrder.shippingOrderGood.shippingOrderGoodsItems = this.goods
            }
        },
        getPrices(shippingOrder) {
            return shippingOrder.shippingOrderGoods.map(
                ({ id, productionNumber, orderNetPrice, productionCost }) => ({ id, productionNumber, orderNetPrice: this.formatPrice(orderNetPrice), productionCost: this.formatPrice(productionCost) })
            )
        },
        setPrices() {
            this.shippingOrder.shippingOrderGoods = this.shippingOrderGoodsPrices.map((_, index) => {
                return {
                    ...this.shippingOrder.shippingOrderGoods[index],
                    orderNetPrice: parseFloat(this.unformatPrice(this.shippingOrderGoodsPrices[index].orderNetPrice)),
                    productionCost: parseFloat(this.unformatPrice(this.shippingOrderGoodsPrices[index].productionCost))
                }
            })
        },
        indexedShipmentsOrder() {
            this.shippingOrder.shippingOrderPositions = this.shippingOrder.shippingOrderPositions.map((shippingOrderPosition, index) => {
                shippingOrderPosition.shippingOrderPositionPackages = shippingOrderPosition.shippingOrderPositionPackages.map((shippingOrderPositionPackage, index) => {
                    shippingOrderPositionPackage.shippingOrderPositionPackageWares = shippingOrderPositionPackage.shippingOrderPositionPackageWares.map((shippingOrderPositionPackageWare, index) => {
                        return {
                            ...shippingOrderPositionPackageWare,
                            index
                        }
                    })

                    return {
                        ...shippingOrderPositionPackage,
                        index
                    }
                })

                return {
                    ...shippingOrderPosition,
                    index
                }
            })
        },
        indexedShipmentsOrderPositions() {
            this.shippingOrder.shippingOrderPositions = this.shippingOrder.shippingOrderPositions.map((item, index) => ({
                ...item,
                index
            }))
        },
        indexedShipmentsOrderPositionsPackages(shippingOrderPositionIndex) {
            this.shippingOrder.shippingOrderPositions[shippingOrderPositionIndex].shippingOrderPositionPackages = this.shippingOrder.shippingOrderPositions[shippingOrderPositionIndex].shippingOrderPositionPackages.map((item, index) => ({
                ...item,
                index
            }))
        },
        indexedShipmentsOrderPositionPackageWares(shippingOrderPositionPackageIndex, shippingOrderPositionIndex) {
            this.shippingOrder.shippingOrderPositions[shippingOrderPositionIndex].shippingOrderPositionPackages[shippingOrderPositionPackageIndex].shippingOrderPositionPackageWares = this.shippingOrder.shippingOrderPositions[shippingOrderPositionIndex].shippingOrderPositionPackages[shippingOrderPositionPackageIndex].shippingOrderPositionPackageWares.map((item, index) => ({
                ...item,
                index
            }))

        },
        parseAddress(shippingOrderPosition) {
            return shippingOrderPosition.street
                ? `${this.parseEmptyValue(shippingOrderPosition.companyName)}, ${shippingOrderPosition.street}, ${shippingOrderPosition.postCode} ${shippingOrderPosition.city}, ${this.parseEmptyValue(shippingOrderPosition.country && shippingOrderPosition.country.name)}`
                : 'Brak adresu'
        },
        showShipmentsOrderPositionDialog(shippingOrderPosition, event) {
            event.stopPropagation()

            if (shippingOrderPosition) {
                addSentryBreadcrumb('ui.action', 'Edycja adresu')
            } else {
                addSentryBreadcrumb('ui.action', 'Dodawanie adresu')
            }

            this.currentShipmentsOrderPosition = shippingOrderPosition

            Promise.all([
                this.$store.dispatch('customers/getCustomerAddresses', this.shippingOrder.shippingOrderCustomer.customerId),
                this.$store.dispatch('shipments/shipmentsOrders/getTransportTypes'),
                this.$store.dispatch('customers/getCustomerAddresses', appConfig.shipping.shippingOrders.headquartersContactId)
            ])
                .then(response => {
                    const totalWeight = shippingOrderPosition ? this.getTotalWeightForPosition(shippingOrderPosition) : 0

                    this.totalWareWeightAmount = totalWeight
                
                    this.shippingOrderPositionDialog = true
                    this.customerContacts = response[0].data.results
                    this.transportTypes = response[1].data.results
                    this.headquartersContact = response[2].data.results[0]
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        saveShipmentsOrderPosition(shippingOrderPositionData) {
            this.hasShipmentsOrderChanges = true

            if (!shippingOrderPositionData.shippingOrderPositionPackages) {
                shippingOrderPositionData.shippingOrderPositionPackages = []
            }

            const index = this.shippingOrder.shippingOrderPositions.findIndex(item => item.index === shippingOrderPositionData.index)

            if (index >= 0) {
                this.shippingOrder.shippingOrderPositions.splice(index, 1, shippingOrderPositionData)
            } else {
                this.shippingOrder.shippingOrderPositions.push(shippingOrderPositionData)
                this.indexedShipmentsOrderPositions()
            }

            // Updated if transportEditableOnly 
            if (!this.isEditable) {
                this.saveShipmentsOrder()
            }

            this.closeShipmentsOrderPositionDialog()
        },
        closeShipmentsOrderPositionDialog() {
            this.shippingOrderPositionDialog = false

            setTimeout(() => {
                this.currentShipmentsOrderPosition = null
            }, 500)
        },
        showConfirmRemoveShipmentsOrderPositionDialog(shippingOrderPosition, event) {
            event.stopPropagation()

            this.confirmRemoveShipmentsOrderPositionDialog = true
            this.currentShipmentsOrderPosition = shippingOrderPosition
        },
        removeShipmentsOrderPosition(currentShipmentsOrderPosition) {
            const shippingOrderPositionIndex = this.shippingOrder.shippingOrderPositions.findIndex(item => item.index === currentShipmentsOrderPosition.index)

            this.shippingOrder.shippingOrderPositions.splice(shippingOrderPositionIndex, 1)

            this.hasShipmentsOrderChanges = true
            this.indexedShipmentsOrderPositions()
            this.closeConfirmRemoveShipmentsOrderPositionDialog()
        },
        closeConfirmRemoveShipmentsOrderPositionDialog() {
            this.confirmRemoveShipmentsOrderPositionDialog = false

            setTimeout(() => {
                this.currentShipmentsOrderPosition = null
            }, 500)
        },
        showShipmentsOrderPositionPackageDialog(shippingOrderPositionPackage, shippingOrderPositionIndex, event) {
            event.stopPropagation()

            Promise.all([this.getPackageBoxTypes(), this.getPackagesTypes()])
                .then(() => {
                    this.currentShipmentsOrderPositionIndex = shippingOrderPositionIndex
                    this.currentShipmentsOrderPositionPackage = shippingOrderPositionPackage

                    this.shippingOrderPositionPackageDialog = true
                })
                .catch(error => errorHandler(error))
        },
        saveShipmentsOrderPositionPackage(shippingOrderPositionPackageData) {
            this.hasShipmentsOrderChanges = true

            const index = this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages.findIndex(item => item.index === shippingOrderPositionPackageData.index)

            if (index >= 0) {
                this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages.splice(index, 1, shippingOrderPositionPackageData)
            } else {
                this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages.push(shippingOrderPositionPackageData)
                this.indexedShipmentsOrderPositionsPackages(this.currentShipmentsOrderPositionIndex)
            }

            this.recalculatePackageAmount(shippingOrderPositionPackageData)

            this.closeShipmentsOrderPositionPackageDialog()
        },
        closeShipmentsOrderPositionPackageDialog() {
            this.shippingOrderPositionPackageDialog = false

            setTimeout(() => {
                this.currentShipmentsOrderPositionPackage = null
                this.currentShipmentsOrderPositionIndex = null
            }, 500)
        },
        showConfirmRemoveShipmentsOrderPositionPackageDialog(shippingOrderPositionPackage, shippingOrderPositionIndex, event) {
            event.stopPropagation()

            this.confirmRemoveShipmentsOrderPositionPackageDialog = true
            this.currentShipmentsOrderPositionPackage = shippingOrderPositionPackage
            this.currentShipmentsOrderPositionIndex = shippingOrderPositionIndex
        },
        removeShipmentsOrderPositionPackage(shippingOrderPositionPackage) {
            const shippingOrderPositionPackageIndex = this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages.findIndex(item => item.index === shippingOrderPositionPackage.index)

            this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages.splice(shippingOrderPositionPackageIndex, 1)

            this.hasShipmentsOrderChanges = true
            this.indexedShipmentsOrderPositionsPackages(this.currentShipmentsOrderPositionIndex)
            this.closeConfirmRemoveShipmentsOrderPositionPackageDialog()
        },
        closeConfirmRemoveShipmentsOrderPositionPackageDialog() {
            this.confirmRemoveShipmentsOrderPositionPackageDialog = false

            setTimeout(() => {
                this.currentShipmentsOrderPositionPackage = null
            }, 500)
        },
        showShipmentsOrderPositionPackageWareDialog(shippingOrderPositionPackageWare, shippingOrderPositionPackageIndex, shippingOrderPositionIndex, shippingOrderPositionPackage, event) {
            event.stopPropagation()

            this.getPackagesTypes().then(() => {
                this.currentShipmentsOrderPositionIndex = shippingOrderPositionIndex
                this.currentShipmentsOrderPositionPackage = shippingOrderPositionPackage
                this.currentShipmentsOrderPositionPackageIndex = shippingOrderPositionPackageIndex
                this.currentShipmentsOrderPositionPackageWare = shippingOrderPositionPackageWare

                this.shippingOrderPositionPackageWareDialog = true
            })
                .catch(error => errorHandler(error))
        },
        saveShipmentsOrderPositionPackageWare(shippingOrderPositionPackageWare) {
            this.hasShipmentsOrderChanges = true
            const index = this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages[this.currentShipmentsOrderPositionPackageIndex].shippingOrderPositionPackageWares.findIndex(item => item.index === shippingOrderPositionPackageWare.index)

            if (index >= 0) {
                this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages[this.currentShipmentsOrderPositionPackageIndex].shippingOrderPositionPackageWares.splice(index, 1, shippingOrderPositionPackageWare)
            } else {
                this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages[this.currentShipmentsOrderPositionPackageIndex].shippingOrderPositionPackageWares.push(shippingOrderPositionPackageWare)
                this.indexedShipmentsOrderPositionPackageWares(this.currentShipmentsOrderPositionPackageIndex, this.currentShipmentsOrderPositionIndex)
            }

            this.recalculatePackageAmount(this.currentShipmentsOrderPositionPackage)

            this.closeShipmentsOrderPositionPackageWareDialog()
        },
        closeShipmentsOrderPositionPackageWareDialog() {
            this.shippingOrderPositionPackageWareDialog = false

            setTimeout(() => {
                this.currentShipmentsOrderPositionIndex = null
                this.currentShipmentsOrderPositionPackage = null
                this.currentShipmentsOrderPositionPackageIndex = null
                this.currentShipmentsOrderPositionPackageWare = null
            }, 500)
        },
        showConfirmRemoveShipmentsOrderPositionPackageWareDialog(shippingOrderPositionPackageWare, shippingOrderPositionPackageIndex, shippingOrderPositionIndex, shippingOrderPositionPackage, event) {
            event.stopPropagation()

            this.confirmRemoveShipmentsOrderPositionPackageWareDialog = true
            this.currentShipmentsOrderPositionPackage = shippingOrderPositionPackage
            this.currentShipmentsOrderPositionPackageIndex = shippingOrderPositionPackageIndex
            this.currentShipmentsOrderPositionIndex = shippingOrderPositionIndex
            this.currentShipmentsOrderPositionPackageWare = shippingOrderPositionPackageWare
        },
        showConfirmCopyShipmentsOrderPositionDialog(shippingOrderPosition, event) {
            event.stopPropagation()
            
            this.confirmCopyShipmentsOrderPositionDialog = true
            this.currentShipmentsOrderPosition = shippingOrderPosition
        },
        copyShipmentsOrderPositionDialog() {
            this.shippingOrder.shippingOrderPositions.forEach((position, index) => {
                this.shippingOrder.shippingOrderPositions[index] = {
                    ...position,
                    city: this.currentShipmentsOrderPosition.city,
                    street: this.currentShipmentsOrderPosition.street,
                    country: this.currentShipmentsOrderPosition.country,
                    postCode: this.currentShipmentsOrderPosition.postCode,
                    countryId: this.currentShipmentsOrderPosition.countryId,
                    countryName: this.currentShipmentsOrderPosition.countryName,
                    companyName: this.currentShipmentsOrderPosition.companyName,
                    phoneNumber: this.currentShipmentsOrderPosition.phoneNumber,
                    contactPerson: this.currentShipmentsOrderPosition.contactPerson,
                    deliveryDateTime: this.currentShipmentsOrderPosition.deliveryDateTime,
                    expeditionDateTime: this.currentShipmentsOrderPosition.expeditionDateTime,
                    deliveryTemplateDateTime: this.currentShipmentsOrderPosition.expeditionDateTime,
                    expeditionTemplateDateTime: this.currentShipmentsOrderPosition.expeditionTemplateDateTime,
                    cost: this.currentShipmentsOrderPosition.cost,
                    costCommaSeparated: this.currentShipmentsOrderPosition.costCommaSeparated,
                    transportType: this.currentShipmentsOrderPosition.transportType,
                    distance: this.currentShipmentsOrderPosition.distance,

                }
            })
            this.hasShipmentsOrderChanges = true
            this.indexedShipmentsOrderPositions()

            this.$store.dispatch('showNotification', {
                type: 'success',
                message: 'Adres został skopiowany.'
            })
            this.closeConfirmCopyShipmentsOrderPositionDialog()
        },
        closeConfirmCopyShipmentsOrderPositionDialog() {
            this.confirmCopyShipmentsOrderPositionDialog = false

            setTimeout(() => {
                this.currentShipmentsOrderPosition = null
            }, 500)
        },
        removeShipmentsOrderPositionPackageWare(shippingOrderPositionPackageWare) {
            const shippingOrderPositionPackageWareIndex = this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages[this.currentShipmentsOrderPositionPackageIndex].shippingOrderPositionPackageWares.findIndex(item => item.index === shippingOrderPositionPackageWare.index)

            this.shippingOrder.shippingOrderPositions[this.currentShipmentsOrderPositionIndex].shippingOrderPositionPackages[this.currentShipmentsOrderPositionPackageIndex].shippingOrderPositionPackageWares.splice(shippingOrderPositionPackageWareIndex, 1)

            this.hasShipmentsOrderChanges = true

            this.recalculatePackageAmount(this.currentShipmentsOrderPositionPackage)

            this.indexedShipmentsOrderPositionPackageWares(this.currentShipmentsOrderPositionPackageIndex, this.currentShipmentsOrderPositionIndex)
            this.closeConfirmRemoveShipmentsOrderPositionPackageWareDialog()
        },
        closeConfirmRemoveShipmentsOrderPositionPackageWareDialog() {
            this.confirmRemoveShipmentsOrderPositionPackageWareDialog = false

            setTimeout(() => {
                this.currentShipmentsOrderPositionPackage = null
                this.currentShipmentsOrderPositionPackageIndex = null
                this.currentShipmentsOrderPositionPackageWare = null
            }, 500)
        },
        shippingOrderPositionError(shippingOrderPositionIndex) {
            if (this.isEditable) {
                return this.$v.shippingOrder.shippingOrderPositions.$each[shippingOrderPositionIndex].$invalid
            }
        },
        shippingOrderPositionPackageError(shippingOrderPositionIndex, shippingOrderPositionPackageIndex) {
            if (this.isEditable) {
                return this.$v.shippingOrder.shippingOrderPositions.$each[shippingOrderPositionIndex].shippingOrderPositionPackages.$each[shippingOrderPositionPackageIndex].$invalid
            }
        },
        shippingOrderPositionPackageWaresError(shippingOrderPositionIndex, shippingOrderPositionPackageIndex, packageWare) {
            if (this.isEditable) {
                return this.$v.shippingOrder.shippingOrderPositions.$each[shippingOrderPositionIndex].shippingOrderPositionPackages.$each[shippingOrderPositionPackageIndex].shippingOrderPositionPackageWares.$each[packageWare.index].$invalid
            }
        },
        saveShipmentsOrder() {
            this.savePaperWasteChanges()
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.actionLoading.saveShipmentsOrder = true
            this.setPrices()
            this.$store.dispatch('shipments/shipmentsOrders/updateShipmentsOrder', this.shippingOrder)
                .then(response => {
                    this.shippingOrder = response.data
                    this.shippingOrderGoodsPrices = JSON.parse(JSON.stringify(this.getPrices(this.shippingOrder)))
                    this.goodsItems = JSON.parse(JSON.stringify(response.data.products))
                    this.hasShipmentsOrderChanges = false
                    this.indexedShipmentsOrder()

                    if (!this.isAllProductsAssigned() || this.isSomeWareNotAssigned()) {
                        this.$store.dispatch('showNotification', {
                            type: 'warning',
                            message: 'Zlecenie spedycyjne zostało zapisane. Pozostał towar nie przypisany do adresu.'
                        })
                    }

                    this.$v.$reset()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.saveShipmentsOrder = false
                })
        },
        sendShipmentsOrder() {
            this.actionLoading.sendShipmentsOrder = true

            this.$store.dispatch('shipments/shipmentsOrders/updateShipmentsOrder', {
                ...this.shippingOrder,
                statusId: 90 // Approved shipping order
            })
                .then(response => {
                    this.shippingOrder = response.data
                    this.$v.$reset()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.sendShipmentsOrder = false
                })
        },
        closeShipmentsOrder() {
            this.$v.$reset()
            this.$router.push({ name: this.routeParentName })
                .catch(error => errorHandler(error))
        },
        closeConfirmLeaveDialog() {
            this.confirmLeaveDialog = false
        },
        acceptConfirmLeaveDialog() {
            this.hasShipmentsOrderChanges = false

            this.$router.push({ name: this.confirmLeaveDialogNextRouteName })
                .catch(error => {
                    errorHandler(error)
                })
        },
        recalculatePackageAmount(shippingOrderPositionPackageData) {
            const packageMaxWeightCapacity = shippingOrderPositionPackageData.maxWeightCapacity != 0 
                ? shippingOrderPositionPackageData.maxWeightCapacity
                : shippingOrderPositionPackageData.packageType.maxWeightCapacity

            if (packageMaxWeightCapacity && shippingOrderPositionPackageData.shippingOrderPositionPackageWares.length > 0) {
                let waresWeight = 0
                let totalBoxesWeight = 0

                shippingOrderPositionPackageData.shippingOrderPositionPackageWares.forEach(ware => {
                    waresWeight += this.totalWareWeight(ware)
                    totalBoxesWeight += this.boxesWareWeight(ware)
                })

                if (!shippingOrderPositionPackageData.setManuallyAmount) {
                    shippingOrderPositionPackageData.amount = Math.ceil((totalBoxesWeight + waresWeight) / packageMaxWeightCapacity)
                }
            } else {
                shippingOrderPositionPackageData.amount = 0
                shippingOrderPositionPackageData.setManuallyAmount = false
            }
        },
        productAmount(ware) {
            return this.shippingOrder.products.find(product => product.name === ware.wareName).amount
        },
        totalWareWeight(ware) {
            const productWeight = this.shippingOrder.products.find(product => product.name === ware.wareName).weightSingle

            return productWeight * ware.wareAmount
        },
        boxesWareWeight(ware) {
            if (!ware.wareUnitPackageTypeId || !ware.wareUnitPackageTypeId) return 0
            
            const packageBox = this.packageTypes.find(box => box.id === ware.wareUnitPackageTypeId)

            return packageBox ? +packageBox.weight * ware.wareUnitPackageQuantity : 0 
        },
        bruttoWeight(shippingOrderPositionPackage) {
            let bruttoWeight = 0

            shippingOrderPositionPackage.shippingOrderPositionPackageWares.forEach(ware => {
                bruttoWeight += this.totalWareWeight(ware) + this.boxesWareWeight(ware)
            })
            bruttoWeight += shippingOrderPositionPackage.packageWeight * shippingOrderPositionPackage.amount

            return bruttoWeight
        },
        isSomeWareNotAssigned() {
            return Object.values(this.$v.shippingOrder.shippingOrderPositions.$each.$iter).some(shippingOrderPosition => {
                return Object.values(shippingOrderPosition.shippingOrderPositionPackages.$each.$iter).some(shippingOrderPositionPackage => {
                    return Object.values(shippingOrderPositionPackage.shippingOrderPositionPackageWares.$each.$iter).some(shippingOrderPositionPackageWare => {
                        return shippingOrderPositionPackageWare.$invalid
                    })
                })
            })
        },
        isAllProductsAssigned() {
            return this.goodsItems.every(good => {
                return this.shippingOrder.shippingOrderPositions.find(shippingOrderPosition => {
                    return shippingOrderPosition.shippingOrderPositionPackages.find(shippingOrderPositionPackage => {
                        return shippingOrderPositionPackage.shippingOrderPositionPackageWares.find(shippingOrderPositionPackageWare => {
                            return shippingOrderPositionPackageWare.wareName === good.name
                        })
                    })
                })
            })
        },
    }
}
</script>
